import React, { createContext, useContext } from "react";

import { CellRendererParams } from "../components/CellRenderers/types";

/**
 * Cell-format override hook.
 *
 * Receives the same params the cell renderer receives, and returns either
 * a string (used as the rendered cell text in place of `valueFormatted`)
 * or `undefined` (fall through to the default rendering pipeline:
 * `params.valueFormatted ?? String(params.value ?? "-")`).
 *
 * Generic over the params shape so callers (e.g., the SBS renderer) can
 * narrow to their specific params type (`SbsCellRendererParams`) without
 * forcing consumers into unsafe casts to access renderer-specific fields.
 *
 * Consumers that wrap PlanEditor (e.g., the Quotes tab) can supply this
 * to inject context-specific display rules — for example,
 * Quotes-specific FIELD_RULES.md formatters — without reaching into the
 * canonical per-LOC `valueFormatter`s in this library.
 */
export type CellFormatOverride<
  TParams extends CellRendererParams = CellRendererParams,
> = (params: TParams) => string | undefined;

const CellFormatOverrideContext = createContext<CellFormatOverride | null>(
  null,
);

interface CellFormatOverrideProviderProps {
  override: CellFormatOverride;
  children: React.ReactNode;
}

export const CellFormatOverrideProvider = ({
  override,
  children,
}: CellFormatOverrideProviderProps): React.JSX.Element => (
  <CellFormatOverrideContext.Provider value={override}>
    {children}
  </CellFormatOverrideContext.Provider>
);

/** Returns the registered override or null if no provider is mounted. */
export const useCellFormatOverride = <
  TParams extends CellRendererParams = CellRendererParams,
>(): CellFormatOverride<TParams> | null =>
  useContext(CellFormatOverrideContext) as CellFormatOverride<TParams> | null;
